/**
 * 이상한 방 - 촉각 피드백(진동)
 *
 * ## 이 파일이 오디오 옆에 있는 이유
 *
 * 진동은 소리가 아니지만 **소리와 같은 층에 산다.** 둘 다 "지금 무슨 일이 일어났는가"를
 * 화면을 가리지 않고 알리는 장치이고(이 게임은 관찰 게임이라 화면을 덮는 피드백은 전부 해악이다),
 * 둘 다 없어도 게임이 완전히 돌아야 하며, 둘 다 호출부가 이름으로만 부른다.
 * `sounds.ts`가 소리의 사전이듯 이 파일의 `HAPTIC_TUNING`이 진동의 사전이다.
 *
 * ## 지원 범위를 먼저 못 박는다
 *
 * | 환경 | Vibration API | 결과 |
 * |---|---|---|
 * | 안드로이드 크롬 · 삼성 인터넷 | 지원 | 울린다 |
 * | **iOS 사파리** | **미지원**(애플이 구현하지 않았다. 홈 화면 PWA로도 안 된다) | 조용한 `false` |
 * | 데스크톱 | 하드웨어 없음 | 조용한 `false` |
 *
 * 즉 **이 기능은 사용자 절반이 절대 경험하지 못한다.** 그러므로 진동은 정보를 나르면 안 된다 -
 * 진동으로만 알 수 있는 것이 하나라도 생기면 그건 아이폰 사용자에게 없는 게임이 된다.
 * 진동은 이미 소리와 화면이 말한 것을 **한 번 더** 말할 뿐이다.
 *
 * ## 조용히 실패한다. 콘솔도 더럽히지 않는다
 *
 * `navigator.vibrate`가 없는데 부르면 던진다. 가드는 당연하고, 그보다 까다로운 것은 **경고**다.
 * 크롬은 사용자 제스처 이전의 `vibrate()` 호출을 막으면서 콘솔에 인터벤션 경고를 찍는다.
 * 그 경고가 쌓이면 진짜 에러가 그 더미에 묻히고, 그때부터 콘솔은 아무도 안 보는 창이 된다.
 * 그래서 `unlock()` 전에는 **호출 자체를 하지 않는다**(`engine.ts`가 AudioContext에 쓰는 것과 같은 규약).
 *
 * ## 구현체를 갈아끼울 수 있게 둔다
 *
 * 나중에 Capacitor로 앱을 내면 네이티브 Haptics가 iOS까지 덮는다. 그때 바꿔야 하는 것은
 * **`HapticsBackend` 하나**이고 호출부는 한 줄도 손대지 않는다.
 * 패턴 이름·쿨다운·설정 저장은 전부 이 파일에 남는다.
 *
 * ```ts
 * const capacitor: HapticsBackend = { id: 'capacitor', isSupported: () => true, vibrate, cancel }
 * const engine = createHaptics({ backend: capacitor })
 * ```
 */

import { installGestureOnce, readJsonSetting, writeJsonSetting } from './storage'

/* --- 패턴 이름 -------------------------------------------------------------------- */

/**
 * 진동을 이름으로 부른다. 호출부에 `[30, 40, 30]` 같은 숫자가 흩어지는 순간 튜닝이 불가능해지고,
 * "오답 진동을 조금 짧게"가 전수 검색이 된다.
 *
 * 목록이 짧은 것은 의도다 - 진동은 **정답·오답·중요 사건에만** 쓴다(아래 "과용 금지").
 */
export const HAPTIC_PATTERNS = [
  'hit',
  'miss',
  'allFound',
  'reload',
  'lowAmmo',
  'envelopeImpact',
  'envelopeLegend',
] as const

export type HapticPattern = (typeof HAPTIC_PATTERNS)[number]

/* --- 튜닝 표 --------------------------------------------------------------------- */

/**
 * 패턴 하나.
 *
 * `ms`는 Vibration API의 규약 그대로 **[울림, 쉼, 울림, ...]**이다. 짝수 자리가 진동,
 * 홀수 자리가 정적이다. 그래서 `[30, 40, 30]`은 "30 울리고 40 쉬고 30 울린다"이지
 * 세 번 울리는 것이 아니다.
 */
export type HapticSpec = Readonly<{
  ms: readonly number[]
  /**
   * 앞선 진동이 아직 울리는 중이어도 끼어드는가.
   *
   * 안드로이드의 `vibrate()`는 **덮어쓴다** - 울리는 도중에 다시 부르면 앞의 것이 잘린다.
   * 그래서 기본은 "울리는 동안 새 진동을 버린다"이고, 그 규칙을 깨는 것은 둘뿐이다.
   * 근거는 `gameState.ts`가 오답과 잔탄 경고를 **같은 틱에** 내보낸다는 사실이다 - 그때
   * 손에 남아야 하는 것은 "빗맞았다"가 아니라 "총알이 없다"이다. 전량 발견도 같다(마지막
   * 정답 진동과 같은 틱에 온다).
   */
  preempt: boolean
  /**
   * 같은 패턴을 다시 울리기까지의 최소 간격(ms). 0이면 아래 `MIN_GAP_MS`만 적용된다.
   * `preempt`가 참인 패턴에는 **반드시** 값이 있어야 한다 - 그것이 없으면 끼어들기가 곧 무한 반복이다.
   */
  repeatGapMs: number
}>

/** 첫 정답의 길이(ms). 15는 "느껴지지만 놀라지 않는" 하한이다. 10 아래는 대부분 못 느낀다. */
const HIT_MS = 15

/** 콤보 한 단계마다 더할 길이(ms). 15 → 25 → 35. */
const HIT_COMBO_STEP_MS = 10

/**
 * 콤보 진동의 상한(ms).
 *
 * 상한이 없으면 12콤보에서 135ms가 되고, 그건 타격이 아니라 **알림**이다(그리고 배터리다).
 * 소리 쪽 콤보가 `AUDIO_TUNING.hit.maxStep`으로 반음을 12에서 멈추는 것과 같은 이유 -
 * 상승은 느껴지되 끝이 있어야 상승으로 읽힌다.
 */
const HIT_COMBO_MAX_MS = 35

/** 잔탄 경고를 다시 울리기까지(ms). 한 라운드에 임계선·바닥 두 번뿐이라 넉넉히 둔다. */
const LOW_AMMO_REPEAT_GAP_MS = 700

/** 전량 발견을 다시 울리기까지(ms). 라운드에 한 번뿐인 사건이므로 사실상 중복 방지용이다. */
const ALL_FOUND_REPEAT_GAP_MS = 1500

/**
 * 봉투의 두 진동을 다시 울리기까지(ms). 열 장 뽑기에서 SR 카드가 잇달아 나올 수 있고, 그때 한 장의
 * 길이(가장 짧은 SR도 1.3초대)보다 짧게 잡아야 두 번째 장이 조용해지지 않는다.
 */
const ENVELOPE_REPEAT_GAP_MS = 900

/**
 * 진동 사전. 숫자를 만질 곳은 여기 하나다.
 *
 * | 패턴 | 손에 남는 것 | 왜 이 모양인가 |
 * |---|---|---|
 * | `hit` | 톡 | 가장 자주 울린다. 짧을수록 좋다 |
 * | `miss` | 둑 · 둑 | **정답과 절대 헷갈리면 안 된다.** 정답이 하나면 오답은 둘이다 |
 * | `allFound` | 두 · 둥 · 쾅 | 이 게임에서 가장 긴 진동. 점점 길어지는 것이 "끝났다"이다 |
 * | `reload` | 딸깍 | 짧고 촘촘한 둘. 실제 장전의 물리다 |
 * | `lowAmmo` | 틱 ‥ 틱 | 짧고 날카롭게. 쉬는 구간이 넓어 `reload`의 딸깍과 갈린다 |
 * | `envelopeImpact` | 쿵 | SR·SSR이 꽂히는 순간. 한 방이라 라운드의 어느 진동과도 안 헷갈린다 |
 * | `envelopeLegend` | 쿵 · 쿵 · 쿠웅 | SSR에만. 셋 중 마지막이 가장 길다 - "더 있다"가 손에 남는다 |
 *
 * 봉투 둘은 **라운드 밖(단계 선택 화면)에서만** 울린다. 목록을 짧게 두는 규칙(위 "과용 금지")을 어기는 것이
 * 아닌 이유가 그것이다 - 수색 중에 손이 떠는 횟수는 한 번도 늘지 않았다. 그리고 아래 등급(N·R)에는
 * 진동이 없다: 소리와 빛이 그러하듯 **조용한 아래**가 위 둘의 값어치다(`ui/envelopeCeremony.ts` 머리말).
 */
export const HAPTIC_TUNING: Readonly<Record<HapticPattern, HapticSpec>> = {
  hit: { ms: [HIT_MS], preempt: false, repeatGapMs: 0 },
  miss: { ms: [30, 40, 30], preempt: false, repeatGapMs: 0 },
  allFound: { ms: [40, 60, 80], preempt: true, repeatGapMs: ALL_FOUND_REPEAT_GAP_MS },
  reload: { ms: [10, 30, 10], preempt: false, repeatGapMs: 0 },
  lowAmmo: { ms: [10, 60, 10], preempt: true, repeatGapMs: LOW_AMMO_REPEAT_GAP_MS },
  // 전량 발견(180ms)보다 짧다 - 라운드에서 가장 긴 진동이라는 자리를 봉투가 빼앗지 않는다.
  envelopeImpact: { ms: [45], preempt: false, repeatGapMs: ENVELOPE_REPEAT_GAP_MS },
  envelopeLegend: { ms: [25, 40, 25, 40, 45], preempt: true, repeatGapMs: ENVELOPE_REPEAT_GAP_MS },
}

/* --- 과용 방지 -------------------------------------------------------------------- */

/**
 * 두 진동 사이의 최소 간격(ms).
 *
 * **이 상수가 "과용 금지"의 실행부다.** 호출부의 선의에 맡기지 않는 이유는 남용이 악의가
 * 아니라 사고이기 때문이다 - 리듀서가 한 틱에 효과를 둘 내보내거나, 자동 사격이 초당 다섯 발을
 * 쏘거나, 누군가 매 탭에 `vibrate('hit')`를 붙이면 배터리는 줄고 손은 짜증만 기억한다.
 *
 * 실제 대기 시간은 `max(MIN_GAP_MS, 방금 울린 패턴의 총 길이)`다. 뒷항이 필요한 이유는
 * 덮어쓰기다 - 180ms짜리 전량 발견 진동이 70ms 만에 정답 진동에 잘리면 축포가 톡 소리가 된다.
 */
const MIN_GAP_MS = 120

/** 한 마디의 상한(ms). 표는 손으로 쓰지만 `hitCombo`는 계산이라 방어선이 필요하다. */
const MAX_ENTRY_MS = 400

/** 패턴 전체의 상한(ms). 여기를 넘는 진동은 피드백이 아니라 고장이다. */
const MAX_TOTAL_MS = 1000

/* --- 백엔드 ---------------------------------------------------------------------- */

/**
 * 진동을 실제로 내는 구현체. **이 게임이 플랫폼에 대해 아는 것은 이 인터페이스가 전부다.**
 *
 * `vibrate`는 던지지 않고 성패를 `boolean`으로 돌려준다. 예외를 밖으로 흘리면 사격 루프가
 * 멈추고, 그건 진동이 없는 게 아니라 게임이 없는 것이다.
 */
export type HapticsBackend = Readonly<{
  /** 디버깅·테스트 식별용. */
  id: string
  isSupported: () => boolean
  /** `[울림, 쉼, ...]`. 실제로 울렸으면 `true`. */
  vibrate: (durationsMs: readonly number[]) => boolean
  /** 울리는 중인 진동을 끊는다. */
  cancel: () => void
}>

/**
 * `navigator`에서 보는 두 필드. `navigator` 자체가 없는 런타임(순수 Node)이 있어 전부 선택 필드다.
 * `maxTouchPoints`는 진동 **하드웨어**의 대리 지표다(아래 `isSupported` 참고).
 */
type VibrationNavigator = {
  vibrate?: (pattern: number | readonly number[]) => boolean
  maxTouchPoints?: number
}

/**
 * 전역에서 `navigator`를 꺼낸다. **속성 접근 자체를 감싼다** - 샌드박스된 iframe이나 일부
 * WebView는 전역을 읽는 것만으로 던진다.
 */
function vibrationNavigator(): VibrationNavigator | null {
  try {
    return (globalThis as { navigator?: VibrationNavigator }).navigator ?? null
  } catch {
    return null
  }
}

/**
 * 표준 Vibration API 백엔드. 안드로이드 크롬·삼성 인터넷에서만 실제로 울린다.
 *
 * `nav.vibrate(...)`로 부르고 함수를 따로 빼두지 않는 것은 취향이 아니다 - `vibrate`를
 * 변수에 담아 부르면 `this`가 끊겨 일부 엔진이 Illegal invocation으로 던진다.
 */
export function vibrationApiBackend(): HapticsBackend {
  return {
    id: 'vibration-api',
    isSupported(): boolean {
      const nav = vibrationNavigator()
      if (nav === null || typeof nav.vibrate !== 'function') return false
      // 함수의 유무만으로는 답이 안 된다 - 데스크톱 크롬은 `vibrate`를 갖고 있으면서 아무것도 울리지
      // 않는다(파일 상단 지원표의 "하드웨어 없음"). 진동 장치가 있는 기기는 전부 터치 기기이므로
      // 터치 지점 수로 거른다. 이 판정은 한쪽으로만 틀린다 - 터치 데스크톱에서 헛되이 켜질 뿐
      // 진짜 폰을 거르지는 않는다. 반대로 틀리는 판정은 안드로이드 사용자에게서 기능을 통째로 빼앗는다.
      return (nav.maxTouchPoints ?? 0) > 0
    },
    vibrate(durationsMs: readonly number[]): boolean {
      try {
        const nav = vibrationNavigator()
        if (nav === null || typeof nav.vibrate !== 'function') return false
        // 크롬은 제스처 전 호출을 막고 `false`를 준다. 엔진이 그 전에 걸러 여기까지 오지 않는다.
        return nav.vibrate([...durationsMs]) !== false
      } catch {
        return false
      }
    },
    cancel(): void {
      try {
        const nav = vibrationNavigator()
        if (nav === null || typeof nav.vibrate !== 'function') return
        // 0은 규약상 "지금 울리는 것을 멈춰라"다.
        nav.vibrate(0)
      } catch {
        // 멈추지 못해도 진동은 어차피 100ms 안에 끝난다.
      }
    },
  }
}

/** 아무것도 하지 않는 백엔드. 테스트와 "진동 없는 빌드"의 기본값이다. */
export const SILENT_BACKEND: HapticsBackend = {
  id: 'silent',
  isSupported: () => false,
  vibrate: () => false,
  cancel: () => undefined,
}

/* --- 설정 저장 -------------------------------------------------------------------- */

/** 저장 키. `weird-room:audio`·`weird-room:progress`와 같은 규약이다. */
export const HAPTICS_STORAGE_KEY = 'weird-room:haptics'

/**
 * 기본값은 **진동 켬**이다.
 *
 * 끔으로 두면 이 기능은 존재하지 않는 것과 같다 - 설정을 열어 보는 사람은 소수이고,
 * 진동은 "켜 보니 좋더라"로 발견되는 종류가 아니다. 반대로 켬으로 두는 비용은 낮다.
 * 지원하지 않는 기기(아이폰·데스크톱)에서는 이 값이 아예 작동하지 않고, 안드로이드에서
 * 거슬리면 토글이 HUD에 있으며 그 선택은 영구히 남는다.
 */
const DEFAULT_ENABLED = true

export type HapticsSettings = Readonly<{ enabled: boolean }>

/**
 * 저장소 배관(접근·파싱 예외를 삼키는 것)은 `storage.ts`가 맡는다. 여기는 **모양 검사와 기본값**만
 * 안다 - 진동 설정 하나 때문에 게임이 안 켜질 수는 없으므로, 무엇이 와도 기본값으로 접는다.
 */
export function readHapticsSettings(): HapticsSettings {
  const parsed = readJsonSetting(HAPTICS_STORAGE_KEY)
  if (typeof parsed !== 'object' || parsed === null) return { enabled: DEFAULT_ENABLED }
  const record = parsed as Readonly<Record<string, unknown>>
  const value = record['enabled']
  return { enabled: typeof value === 'boolean' ? value : DEFAULT_ENABLED }
}

export function writeHapticsSettings(settings: HapticsSettings): void {
  // 객체로 감싸는 이유는 확장이다 - 나중에 세기(intensity)가 붙어도 저장 포맷이 안 깨진다.
  writeJsonSetting(HAPTICS_STORAGE_KEY, { enabled: settings.enabled })
}

/* --- 콤보 길이 -------------------------------------------------------------------- */

/**
 * 콤보 단계 → 진동 길이(ms). **`comboStep`은 0부터다**(콤보 1단계 = 0).
 * 오디오(`sound('hit', step)`)·연출(`juice.hit`)과 같은 규약이라 호출부가 값을 변환하지 않는다.
 *
 * 음수·NaN을 기본 길이로 접는 이유는 호출부다. 콤보는 게임 상태에서 오고, 그 값이 잠깐
 * 이상해도 **진동이 던져서 사격이 멈추면 안 된다.**
 */
export function comboVibrationMs(comboStep: number): number {
  if (!Number.isFinite(comboStep) || comboStep <= 0) return HIT_MS
  return Math.min(HIT_MS + Math.floor(comboStep) * HIT_COMBO_STEP_MS, HIT_COMBO_MAX_MS)
}

/* --- 패턴 검사 -------------------------------------------------------------------- */

function clampMs(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0
  return Math.min(Math.round(value), MAX_ENTRY_MS)
}

/**
 * 백엔드에 넘길 수 있는 형태로 다듬는다. 울릴 것이 없으면 `null`.
 *
 * 꼬리의 쉼을 잘라내는 이유는 그것이 순수한 낭비이기 때문이다 - `[15, 200]`은 15ms 울리고
 * 200ms 동안 **아무 일도 하지 않으면서** 진동 장치를 붙잡는다.
 */
function sanitize(ms: readonly number[]): readonly number[] | null {
  const out: number[] = []
  let total = 0

  for (const raw of ms) {
    const clamped = clampMs(raw)
    if (total + clamped > MAX_TOTAL_MS) break
    total += clamped
    out.push(clamped)
  }

  while (out.length > 0 && out[out.length - 1] === 0) out.pop()
  // 짝수 자리가 진동이다. 거기가 전부 0이면 쉬기만 하는 패턴이라 부를 이유가 없다.
  if (!out.some((value, index) => index % 2 === 0 && value > 0)) return null

  return out
}

function totalMs(ms: readonly number[]): number {
  return ms.reduce((sum, value) => sum + value, 0)
}

/* --- 엔진 ------------------------------------------------------------------------ */

/**
 * 시계. **`performance.now()`를 먼저 본다** - 단조 증가라 시스템 시각이 뒤로 점프해도
 * 쿨다운이 얼어붙지 않는다. 게임 판정에 쓰는 시각이 아니므로 결정론 제약과 무관하다.
 */
function defaultNow(): number {
  const perf = (globalThis as { performance?: { now?: () => number } }).performance
  if (perf !== undefined && typeof perf.now === 'function') return perf.now()
  return Date.now()
}

export type HapticsOptions = Readonly<{
  /** 구현체. 넘기지 않으면 표준 Vibration API. Capacitor 네이티브는 여기로 들어온다. */
  backend?: HapticsBackend
  /** 초기 설정. 넘기지 않으면 localStorage에서 읽는다(테스트는 저장소를 건드리지 않는다). */
  settings?: HapticsSettings
  /** 설정을 저장할지. 테스트는 끈다. */
  persist?: boolean
  /** 시계. 쿨다운 테스트가 시간을 직접 민다. */
  now?: () => number
}>

export interface HapticsEngine {
  /**
   * 첫 사용자 제스처 안에서 부른다. **이 전에는 백엔드를 부르지 않는다** -
   * 크롬이 콘솔에 인터벤션 경고를 쌓기 때문이다.
   */
  unlock(): void
  /** 첫 제스처를 자동으로 잡아 `unlock()`한다. 돌려주는 함수를 부르면 구독이 끊긴다. */
  installUnlockGesture(target?: EventTarget): () => void
  /** 이름으로 울린다. 실제로 울렸으면 `true`. 미지원·끔·미해제·쿨다운은 전부 조용한 `false`다. */
  vibrate(pattern: HapticPattern): boolean
  /** 정답 진동. 콤보가 오를수록 길어지고 상한에서 멈춘다(`comboVibrationMs`). */
  hitCombo(comboStep: number): boolean
  /** 이 기기가 진동을 낼 수 있는가. HUD가 토글을 감출 때 쓴다(아이폰·데스크톱에서 거짓). */
  isSupported(): boolean
  isEnabled(): boolean
  setEnabled(enabled: boolean): void
  /** 바뀐 뒤의 상태를 돌려준다. HUD 버튼이 이것만 부르면 된다. */
  toggleEnabled(): boolean
  /** 울리는 중인 진동을 끊는다. 라운드가 끝나거나 화면이 가려질 때. 해제 전에는 백엔드를 부르지 않는다. */
  cancel(): void
  dispose(): void
}

/**
 * 백엔드 호출을 감싼다.
 *
 * 기본 백엔드는 스스로 던지지 않는다. 문제는 **갈아끼운 구현체**다 - Capacitor Haptics는
 * 네이티브 브리지가 없거나 끊기면 던지고, 그 예외가 여기를 통과하면 멈추는 것은 진동이 아니라
 * 사격 루프다. 진동이 없는 것과 게임이 없는 것은 다른 사건이고, 이 파일이 막아야 하는 것은 후자다.
 *
 * 삼키기만 하고 알리지 않는 이유는 콘솔이다 - 진동은 프레임마다 불릴 수 있어서, 여기서 한 번
 * 경고를 찍기 시작하면 진짜 에러가 그 더미에 묻힌다(파일 상단의 "조용히 실패한다"와 같은 규약).
 */
function safely<T>(run: () => T, fallback: T): T {
  try {
    return run()
  } catch {
    return fallback
  }
}

export function createHaptics(options: HapticsOptions = {}): HapticsEngine {
  const backend = options.backend ?? vibrationApiBackend()
  const persist = options.persist ?? true
  const now = options.now ?? defaultNow

  let enabled = (options.settings ?? readHapticsSettings()).enabled
  let unlocked = false

  /** 패턴별 마지막 발동 시각. 같은 진동이 연달아 오는 것을 막는다. */
  const lastAt = new Map<HapticPattern, number>()
  /** 이 시각까지는 앞 진동이 울리는 중으로 본다. `preempt`만 끼어든다. */
  let busyUntil = Number.NEGATIVE_INFINITY

  /** 설치한 제스처 구독. `dispose`가 전부 걷어낸다. */
  const gestureRemovers = new Set<() => void>()

  function save(): void {
    if (persist) writeHapticsSettings({ enabled })
  }

  function isSupported(): boolean {
    return safely(() => backend.isSupported(), false)
  }

  function cancelBackend(): void {
    safely(() => backend.cancel(), undefined)
  }

  function unlock(): void {
    unlocked = true
  }

  /**
   * 구독 자체는 `storage.ts`의 공통 배관이 건다. 여기서 더하는 것은 `dispose`가 걷을 수 있게
   * 해제 함수를 등록해 두는 일뿐이다 - 첫 제스처가 스스로 걷으면 등록도 같이 지운다.
   */
  function installUnlockGesture(target?: EventTarget): () => void {
    const remove = (): void => {
      detach()
      gestureRemovers.delete(remove)
    }
    const detach = installGestureOnce(target, () => {
      unlock()
      gestureRemovers.delete(remove)
    })
    gestureRemovers.add(remove)
    return remove
  }

  /**
   * 모든 진동이 지나는 문 하나. 게이트 순서가 곧 정책이라 그대로 읽힌다.
   *
   * 실패 경로가 전부 `false`인 것이 이 파일의 계약이다. 호출부는 결과를 볼 필요조차 없다 -
   * 돌려주는 이유는 테스트와 디버깅뿐이다.
   */
  function fire(pattern: HapticPattern, ms: readonly number[]): boolean {
    if (!enabled || !unlocked) return false
    if (!isSupported()) return false

    const spec = HAPTIC_TUNING[pattern]
    const at = now()
    if (!Number.isFinite(at)) return false

    const previous = lastAt.get(pattern)
    if (previous !== undefined && at - previous < spec.repeatGapMs) return false
    if (at < busyUntil && !spec.preempt) return false

    const durations = sanitize(ms)
    if (durations === null) return false
    if (!safely(() => backend.vibrate(durations), false)) return false

    lastAt.set(pattern, at)
    busyUntil = at + Math.max(MIN_GAP_MS, totalMs(durations))
    return true
  }

  return {
    unlock,
    installUnlockGesture,
    vibrate: (pattern: HapticPattern): boolean => fire(pattern, HAPTIC_TUNING[pattern].ms),
    hitCombo: (comboStep: number): boolean => fire('hit', [comboVibrationMs(comboStep)]),
    isSupported,
    isEnabled: () => enabled,
    setEnabled(next: boolean): void {
      if (next === enabled) return
      enabled = next
      // 끄는 순간 손이 조용해져야 한다. 울리던 것이 남아 있으면 토글이 고장 난 것으로 읽힌다.
      if (!enabled) cancelBackend()
      save()
    },
    toggleEnabled(): boolean {
      enabled = !enabled
      if (!enabled) cancelBackend()
      save()
      return enabled
    },
    cancel(): void {
      // 해제 전에는 백엔드를 부르지 않는다(`fire`와 같은 문). 제스처 전의 `vibrate(0)`도 크롬에게는
      // 제스처 전 호출이라 같은 인터벤션 경고를 찍는다. 그 전에 울린 것도 없으니 끊을 것도 없다.
      if (!unlocked) return
      cancelBackend()
      busyUntil = Number.NEGATIVE_INFINITY
    },
    dispose(): void {
      for (const remove of [...gestureRemovers]) remove()
      cancelBackend()
      lastAt.clear()
      busyUntil = Number.NEGATIVE_INFINITY
      unlocked = false
    },
  }
}

/**
 * 앱이 쓰는 하나뿐인 엔진.
 *
 * 모듈 로드 시점에 만들어도 안전하다 - 생성자는 localStorage만 읽고 **아무것도 울리지 않는다.**
 *
 * 제스처 구독을 여기서 스스로 건다. `engine.ts`가 그러지 않는 것과 다른 이유는 비용이다 -
 * 오디오는 제스처 콜백 **안에서** AudioContext를 만들어야 해서 부트스트랩이 시점을 쥐어야 하지만,
 * 진동은 깃발 하나만 세우면 되고 그 구독은 첫 탭에 스스로 걷힌다. 부트스트랩이 한 줄을
 * 잊으면 진동이 통째로 죽는 쪽이 훨씬 비싸다.
 */
export const haptics: HapticsEngine = createHaptics()

try {
  haptics.installUnlockGesture()
} catch {
  // document가 없는 실행기(순수 Node·워커). 그런 곳에는 진동 장치도 없다.
}
